/**
 * Field keys (in priority order) that may contain disclaimer body text on
 * `report.data` or `fundamental_data` payloads from the API.
 */
export const DISCLAIMER_TEXT_FIELD_KEYS = [
  "saltus_disclaimers",
  "saltus_birchwood_disclaimer",
  "saltus_nlp_disclaimer",
] as const;

/**
 * Context property name for the disclaimer value after API keys are coalesced.
 * Keeps mention resolution logic decoupled from vendor-specific field names.
 */
export const DISCLAIMER_RESOLVED_CONTEXT_KEY = "disclaimer_resolved_payload" as const;

/**
 * Returns whether a disclaimer field value is considered present for resolution.
 *
 * @param value - Raw field value from the API
 * @returns True when the value should be treated as usable disclaimer content
 */
function isPresentDisclaimerValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "object") return true;
  return true;
}

/**
 * Chooses the first non-empty disclaimer payload from a flat record (e.g.
 * `currentReport.data` or `fields.fundamental_data`), using a fixed key order.
 *
 * @param record - Object that may contain one or more disclaimer fields
 * @returns The first matching value, or `null` if none are present
 */
export function pickDisclaimerPayloadFromRecord(
  record: Record<string, unknown> | null | undefined
): unknown {
  if (!record) return null;
  for (const key of DISCLAIMER_TEXT_FIELD_KEYS) {
    const value = record[key];
    if (isPresentDisclaimerValue(value)) {
      return value;
    }
  }
  return null;
}

/**
 * Reads the coalesced disclaimer payload from resolver context: prefers
 * {@link DISCLAIMER_RESOLVED_CONTEXT_KEY}, then the historical single-field shape.
 *
 * @param context - Resolver context object
 * @returns Payload if present, otherwise `undefined`
 */
export function readDisclaimerResolvedFromContext(
  context?: Record<string, unknown> | null
): unknown {
  if (!context) return undefined;
  const resolved = context[DISCLAIMER_RESOLVED_CONTEXT_KEY];
  if (isPresentDisclaimerValue(resolved)) return resolved;
  // Legacy shape: some callers still pass only the historical API field name.
  const legacy = context["saltus_disclaimers"];
  if (isPresentDisclaimerValue(legacy)) return legacy;
  return undefined;
}

/**
 * Flattens a disclaimer API value (string or `{ content: string }`, etc.) to plain text.
 *
 * @param payload - Raw disclaimer field value
 * @returns Text suitable for display or `(n)` scanning
 */
export function disclaimerPayloadToPlainText(payload: unknown): string {
  if (payload == null) return "";
  if (typeof payload === "string") return payload;
  if (typeof payload === "object") {
    const content = (payload as { content?: unknown }).content;
    if (typeof content === "string") return content;
    try {
      return JSON.stringify(payload);
    } catch {
      return "";
    }
  }
  return String(payload);
}
